import React, { lazy, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { PrescriptionService } from '../../prescription/service';
import { PDF_LAYOUT_MODEL_TYPE, PDFModelLayout } from '../../../shared/prescriber';
import { get, isEmpty } from 'lodash';
import BaseDialog from '../../../components/Dialogs/BaseDialog';
import { usePrescriptionStore } from '../../../stores/prescriptionStore';
import withSuspense from '../../../components/withLazyComponent';
import { CustomSelect, CustomMenuItem } from '../../../components/UI-CHAKRA/Select/CustomSelect';
import { DONT_SHOW_RQE_IN_PDF_KEY } from '../constants';
import FreeSampleSidebar from '../../../components/FreeSampleSidebar';
import { mapEstablishmentOptions, mapPrescriberInfo } from '../../../interfaces/freeSample';
import { appViewportHeight } from '../../../helpers/embeddedViewport';
import { RQE } from '../../../interfaces/users';
import { toast } from '../../../helpers/toast';

const PDFLayoutEditor = withSuspense(lazy(() => import('./PDFLayoutEditor')));
const PDFLayoutList = withSuspense(lazy(() => import('./PDFLayoutList')));

export enum LAYOUT_ACTION_TYPES {
  EDIT = 'edit',
  CREATE = 'create',
}

export interface CreateEditLayout {
  layout: PDFModelLayout | null;
  actionType: LAYOUT_ACTION_TYPES;
  layoutType: PDF_LAYOUT_MODEL_TYPE;
}

const RQE_UPDATE_ERROR_MESSAGE = 'Não foi possível alterar o RQE da prescrição.';

function pickDefaultRqeId(list: RQE[]): number | null {
  if (isEmpty(list)) return null;
  return list.reduce((a, b) => (a.idMedicoRqe > b.idMedicoRqe ? a : b)).idMedicoRqe;
}

interface PDFLayoutEditorDialogProps {
  createEditLayout: CreateEditLayout | null;
  onClose(): void;
  goBack(): void;
}

export function PDFLayoutEditorDialog({ createEditLayout, onClose, goBack }: PDFLayoutEditorDialogProps) {
  // Cada abertura monta um editor novo. O diálogo pode reabrir antes de a animação de saída desmontar o
  // editor anterior, e o formulário voltaria com o nome e os switches da abertura passada.
  const [session, setSession] = useState({ createEditLayout, key: 0 });
  if (createEditLayout !== session.createEditLayout) {
    setSession({ createEditLayout, key: createEditLayout ? session.key + 1 : session.key });
  }

  return (
    <BaseDialog
      isOpen={!isEmpty(createEditLayout)}
      persist={false}
      onClose={onClose}
      width={'95vw'}
      stylesCustom={{ backgroundColor: 'transparent', height: appViewportHeight(0.95), boxShadow: 'none' }}
    >
      <PDFLayoutEditor
        key={session.key}
        goBack={goBack}
        layout={get(createEditLayout, 'layout', null)}
        actionType={get(createEditLayout, 'actionType', LAYOUT_ACTION_TYPES.EDIT)}
        layoutType={get(createEditLayout, 'layoutType', 'SIMPLES')}
      />
    </BaseDialog>
  );
}

function ChevronRight() {
  return (
    <svg width="7" height="12" viewBox="0 0 7 12" fill="none" className="shrink-0">
      <path d="M1 1L6 6L1 11" stroke="#3D0E6B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function Toggle({
  checked,
  onChange,
  disabled = false,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <button
      role="switch"
      aria-checked={checked}
      aria-disabled={disabled}
      disabled={disabled}
      onClick={() => !disabled && onChange(!checked)}
      className={`relative inline-flex items-center rounded-full transition-colors border-0 p-0 shrink-0 ${
        disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
      }`}
      style={{
        width: '44px',
        height: '24px',
        backgroundColor: checked ? '#3D0E6B' : '#D1D5DB',
      }}
    >
      <span
        className="inline-block rounded-full bg-white shadow transition-transform"
        style={{
          width: '18px',
          height: '18px',
          transform: checked ? 'translateX(22px)' : 'translateX(3px)',
        }}
      />
    </button>
  );
}

function PrescriberCentral() {
  const { selectedRqe, setSelectedRqe, rqes } = usePrescriptionStore();
  const prescriptionId = usePrescriptionStore((store) => store.prescription?.idPrescricao);
  const prescriber = usePrescriptionStore((store) => store.prescriber());
  const prescriberId = prescriber?.idPrescritor || null;
  const { prescriberConfiguration } = usePrescriptionStore();
  const establishment = usePrescriptionStore((store) => store.prescription?.Estabelecimento);

  const enableCustomPdfLayout = get(prescriberConfiguration, 'Features.enablePdfCustomLayout', false);
  const disableRqe = get(prescriberConfiguration, 'Features.disableRqe', false);
  const enableFreeSample = get(prescriberConfiguration, 'Features.enableFreeSample', false);

  const [isPdfListOpen, setIsPdfListOpen] = useState(false);
  const [createEditLayout, setCreateEditLayout] = useState<CreateEditLayout | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const isSavingRef = useRef(false);
  const [isFreeSampleSidebarOpen, setIsFreeSampleSidebarOpen] = useState(false);

  const freeSampleEstablishmentOptions = mapEstablishmentOptions(establishment);

  const activeRqes = useMemo(() => rqes.filter((r) => r.ativo), [rqes]);

  const showRqe = useMemo(() => selectedRqe !== null && selectedRqe !== DONT_SHOW_RQE_IN_PDF_KEY, [selectedRqe]);

  const parseRqe = useCallback((textoSimples: string) => {
    const match = textoSimples.match(/^(.*?)\s*-\s*RQE\s*N[ºo°:.\s]*([\d]+)/i);
    if (match) {
      return { specialty: match[1].trim(), number: match[2].trim() };
    }
    return { specialty: textoSimples.trim(), number: '' };
  }, []);

  const selectedRqeLabel = useMemo(() => {
    const rqe = activeRqes.find((r) => r.idMedicoRqe === selectedRqe);
    return rqe ? parseRqe(rqe.textoSimples).specialty : '';
  }, [activeRqes, selectedRqe, parseRqe]);

  const hasValidSelection =
    selectedRqe === DONT_SHOW_RQE_IN_PDF_KEY || activeRqes.some((r) => r.idMedicoRqe === selectedRqe);

  const handleChange = useCallback(
    async (value: number) => {
      if (!prescriptionId || !prescriberId || isSavingRef.current) return;
      const isDeselect = value === DONT_SHOW_RQE_IN_PDF_KEY;
      const rqe = activeRqes.find((r) => r.idMedicoRqe === value) ?? null;
      const previous = usePrescriptionStore.getState().selectedRqe;
      isSavingRef.current = true;
      setIsSaving(true);
      setSelectedRqe(value);
      try {
        await PrescriptionService.changeSelectedRqe(prescriptionId, {
          idMedico: prescriberId,
          RQESelecionado: isDeselect ? null : rqe,
        });
      } catch (error) {
        setSelectedRqe(previous);
        toast.error(get(error, 'response.data[0].Descricao', RQE_UPDATE_ERROR_MESSAGE));
      } finally {
        isSavingRef.current = false;
        setIsSaving(false);
      }
    },
    [prescriptionId, prescriberId, activeRqes, setSelectedRqe]
  );

  const handleRqeToggle = useCallback(
    async (enabled: boolean) => {
      if (!enabled) {
        await handleChange(DONT_SHOW_RQE_IN_PDF_KEY);
        return;
      }
      const defaultRqeId = pickDefaultRqeId(activeRqes);
      if (defaultRqeId !== null) await handleChange(defaultRqeId);
    },
    [handleChange, activeRqes]
  );

  const autoSelectAttemptedRef = useRef(false);

  useEffect(() => {
    if (
      disableRqe ||
      hasValidSelection ||
      autoSelectAttemptedRef.current ||
      isSaving ||
      !prescriptionId ||
      !prescriberId
    ) {
      return;
    }
    autoSelectAttemptedRef.current = true;
    handleChange(pickDefaultRqeId(activeRqes) ?? DONT_SHOW_RQE_IN_PDF_KEY);
  }, [disableRqe, hasValidSelection, activeRqes, isSaving, prescriptionId, prescriberId, handleChange]);

  const noRqeAvailable = isEmpty(activeRqes);
  const hasPdfSection = !disableRqe || enableCustomPdfLayout;

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto p-4 flex flex-col gap-3">
        {hasPdfSection && (
          <div className="border border-[#e9ecee] rounded-xl overflow-hidden bg-white">
            <div className="px-4 py-3 border-b border-[#e9ecee]">
              <span className="text-[13px] font-bold text-[#3D0E6B] uppercase tracking-wide">Configurações de PDF</span>
            </div>

            {!disableRqe && (
              <div className="flex flex-col">
                <div
                  className={`flex items-center justify-between px-4 py-[14px] ${enableCustomPdfLayout || (showRqe && !noRqeAvailable) ? 'border-b border-[#f1f3f5]' : ''}`}
                >
                  <span className="text-[14px] text-[#343A40]">Exibir RQE</span>
                  <Toggle
                    checked={!noRqeAvailable && showRqe}
                    onChange={handleRqeToggle}
                    disabled={noRqeAvailable || isSaving}
                  />
                </div>
                {noRqeAvailable && (
                  <div className="mx-4 mb-3 rounded-lg bg-[#FFF4E5] px-3 py-2">
                    <span className="text-[12px] text-[#B36B00]">
                      Não é possível habilitar pois não há RQE vinculado ao CRM informado.
                    </span>
                  </div>
                )}
              </div>
            )}

            {!disableRqe && !noRqeAvailable && showRqe && (
              <div
                className={`px-4 py-[14px] flex flex-col gap-2 ${enableCustomPdfLayout ? 'border-b border-[#f1f3f5]' : ''}`}
              >
                <span className="text-[13px] text-[#868E96]">Selecione o RQE para a prescrição</span>
                <CustomSelect
                  placeholder="RQE"
                  value={selectedRqeLabel}
                  displayValue={selectedRqeLabel}
                  isDisabled={isSaving}
                  data-testid="select-rqe"
                >
                  {activeRqes.map((rqe) => {
                    const { specialty, number } = parseRqe(rqe.textoSimples);
                    return (
                      <CustomMenuItem
                        key={rqe.idMedicoRqe}
                        value={rqe.idMedicoRqe}
                        onClick={() => handleChange(rqe.idMedicoRqe)}
                        data-value={`select-rqe-option-${rqe.idMedicoRqe}`}
                        label={
                          <span className="flex flex-col items-start gap-0.5 py-1">
                            <span className="text-[14px] font-medium not-italic text-[#343A40] leading-tight">
                              {specialty}
                            </span>
                            {number && (
                              <span className="text-[12px] font-normal not-italic text-[#868E96] leading-tight">
                                RQE {number}
                              </span>
                            )}
                          </span>
                        }
                      />
                    );
                  })}
                </CustomSelect>
              </div>
            )}

            {enableCustomPdfLayout && (
              <button
                onClick={() => setIsPdfListOpen(true)}
                className="w-full flex items-center justify-between px-4 py-[14px] hover:bg-gray-50 transition-colors cursor-pointer bg-transparent border-0 text-left"
              >
                <span className="text-[14px] text-[#343A40]">Editar layout</span>
                <ChevronRight />
              </button>
            )}
          </div>
        )}

        {enableFreeSample && (
          <div className="border border-[#e9ecee] rounded-xl overflow-hidden bg-white">
            <button
              onClick={() => setIsFreeSampleSidebarOpen(true)}
              className="w-full flex items-center justify-between px-4 py-[14px] hover:bg-gray-50 transition-colors cursor-pointer bg-transparent border-0 text-left"
            >
              <span className="text-[14px] text-[#343A40]">Amostra grátis</span>
              <ChevronRight />
            </button>
          </div>
        )}

        <FreeSampleSidebar
          isOpen={isFreeSampleSidebarOpen}
          onClose={() => setIsFreeSampleSidebarOpen(false)}
          establishmentOptions={freeSampleEstablishmentOptions}
          prescriber={mapPrescriberInfo(prescriber)}
        />
      </div>

      <BaseDialog
        isOpen={isPdfListOpen}
        persist={false}
        onClose={() => setIsPdfListOpen(false)}
        width={{ base: '95%', md: '650px' }}
        stylesCustom={{ minHeight: appViewportHeight(0.65) }}
      >
        <PDFLayoutList
          onClose={() => setIsPdfListOpen(false)}
          onEdit={(layout: PDFModelLayout | null, layoutType: 'SIMPLES' | 'CONTROLADO') => {
            setIsPdfListOpen(false);
            setCreateEditLayout({ actionType: LAYOUT_ACTION_TYPES.EDIT, layout, layoutType });
          }}
          onCreate={(layoutType: 'SIMPLES' | 'CONTROLADO') => {
            setIsPdfListOpen(false);
            setCreateEditLayout({ actionType: LAYOUT_ACTION_TYPES.CREATE, layout: null, layoutType });
          }}
          isWidget={false}
        />
      </BaseDialog>

      <PDFLayoutEditorDialog
        createEditLayout={createEditLayout}
        onClose={() => setCreateEditLayout(null)}
        goBack={() => {
          setCreateEditLayout(null);
          setIsPdfListOpen(true);
        }}
      />
    </div>
  );
}

export default PrescriberCentral;
